import { Flex, Loader } from "@aws-amplify/ui-react"
import { formatUnicorn, getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useCallback, useEffect, useState } from "react"
import {
  FaChartSimple,
  FaRegCalendarDays,
  FaRegCalendarXmark,
} from "react-icons/fa6"
import { useNavigate, useParams, type NavigateFunction } from "react-router-dom"
import DatePicker from "react-tailwindcss-datepicker"
import type { DateValueType } from "react-tailwindcss-datepicker"
import {
  getCustomRangeFeedingStats,
  getDefaultDateRange,
} from "../../../api/FeedingStatsApi"
import { LoadingErrorState } from "../../../components"

export interface FeedingStatsRenderComponentProps {
  feedingStats: any
  navigate: NavigateFunction
  reload: () => void
}

interface FeedingStatsPageProps {
  title: string
  isDataPresent: (feedingStats: any) => boolean
  RenderComponent: (feedingStats: any) => React.ReactElement
}
export function FeedingStatsPage({
  title,
  isDataPresent,
  RenderComponent,
}: FeedingStatsPageProps) {
  const params = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const orgId = params.orgId!
  const now = new Date()
  const [datePickerValue, setDatePickerValue] = useState<DateValueType>(
    getDefaultDateRange(now)
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [feedingStats, setFeedingStats] = useState<any | null>(null)

  const loadData = useCallback(
    async (dateRange: DateValueType) => {
      try {
        // console.log("> Feedings.useEffect.loadData", dateRange)
        if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
          console.debug("No valid date range -> clear data, skip loading")
          setFeedingStats(null)
          setError("Bitte einen Zeitraum auswählen.")
          return
        }
        setLoading(true)
        setFeedingStats(null)
        setError(undefined)
        const data = await getCustomRangeFeedingStats(
          orgId,
          dateRange!.startDate!,
          dateRange!.endDate!
        )
        setFeedingStats(data)
      } catch (err) {
        console.log("🎉 Caught an error:", JSON.stringify(err, null, 2))
        setFeedingStats(null)
        let errMsg: string
        if ((err as any)["body"]) {
          const body = (err as any)["body"]["error"]
          errMsg = String(body) as string // probably, getErrorMessage would work here, too.
        } else {
          errMsg = getErrorMessage(err)
          // setError(JSON.stringify(err, null, 2))
        }
        if (errMsg.includes("No feeding reports found for time range")) {
          setError(undefined)
        } else {
          setError(errMsg)
        }
      } finally {
        setLoading(false)
      }
    },
    [orgId]
  )

  useEffect(() => {
    if (datePickerValue) {
      loadData(datePickerValue!)
    }
  }, [datePickerValue, loadData])

  const reload = useCallback(
    () => loadData(datePickerValue),
    [loadData, datePickerValue]
  )

  const icon = <FaChartSimple className="w-5 _h-3.5_ relativ text-[#3E9E29]" />

  const onDatePickerChange = (newValue: DateValueType) => {
    newValue?.startDate?.setHours(0, 0, 0, 0)
    newValue?.endDate?.setHours(23, 59, 59, 0)
    setDatePickerValue(newValue)
    localStorage.setItem("userPref:dateRange", JSON.stringify(newValue))
  }

  let content
  if (loading) {
    content = (
      <>
        <LoadingErrorState
          isLoading={loading}
          loadingMessage={I18n.get(
            "Lade Daten... Je nach ausgewähltem Zeitraum kann das einige Sekunden dauern."
          )}
          isError={false}
        />
        <Loader
          filledColor="#498e39"
          variation="linear"
          className="!max-w-xl"
        />
      </>
    )
  } else if (error) {
    content = (
      <LoadingErrorState
        isError={true}
        errorMessage={formatUnicorn(
          I18n.get("Fehler beim Laden der Daten: {errorMessage}"),
          { errorMessage: error }
        )}
        isLoading={loading}
      />
    )
  } else if (!isDataPresent(feedingStats)) {
    content = (
      <LoadingErrorState
        isError={true}
        errorMessage={I18n.get(
          "Keine Statistik-Daten für den ausgewählten Zeitraum vorhanden."
        )}
        isLoading={loading}
      />
    )
  } else {
    content = (
      <RenderComponent
        feedingStats={feedingStats}
        navigate={navigate}
        reload={reload}
      />
    )
  }

  return (
    <div className="rm-list-page">
      <Flex className="!h-12 !pb-2 !justify-between !items-center">
        <Flex className="!h-6 !justify-start !items-center gap-3">
          {icon}
          <div className="text-xl font-semibold">{title}</div>

          <div className="button-with-icon !p-0">
            <DatePicker
              // inputClassName="w-full rounded-md focus:ring-0 font-normal bg-blue-300 placeholder:text-blue-100 text-white dark:bg-blue-900 dark:placeholder:text-blue-100"
              inputClassName="p-0 text-gray-700 outline-none border-none"
              // containerClassName="relative mt-8"
              containerClassName="relative px-3 py-2 datepicker-container"
              // toggleClassName="absolute bg-blue-300 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              toggleClassName="mr-0"
              startFrom={datePickerValue?.startDate}
              value={datePickerValue}
              onChange={onDatePickerChange}
              showShortcuts={true}
              showFooter={false}
              startWeekOn="mon"
              configs={{
                footer: {
                  cancel: "Abbrechen",
                  apply: "Übernehmen",
                },
                shortcuts: {
                  today: "Heute",
                  yesterday: "Gestern",
                  past: (period) =>
                    formatUnicorn("Letzte {period} Tage", { period }),
                  currentMonth: "Aktueller Monat",
                  pastMonth: "Letzter Monat",
                },
              }}
              // i18n={userPrefLang || undefined}
              displayFormat={"DD.MM.YYYY"}
              readOnly={true}
              toggleIcon={(open: boolean) =>
                open ? (
                  <FaRegCalendarDays className="icon inline" />
                ) : (
                  <FaRegCalendarXmark className="icon inline" />
                )
              }
            />
          </div>
        </Flex>
      </Flex>
      {content}
    </div>
  )
}
